import { memo, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from '@hooks';
import useMediaQuery from '@mui/material/useMediaQuery';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import { Typography } from '@components/Typography';
import Line from '@components/Charts/Line';
import { POST_CHANNEL } from '@constants/social-posts';
import { BRAND_TEAL } from '@constants/colors';
import { I18N_DATE_FORMAT, getChartDateFormat } from '@helpers/dates';
import { styled } from '@mui/system';

const PaperCss = styled(Paper)({
    width: '100%',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    marginBottom: '16px',
    '@media print': {
        breakInside: 'avoid',
    },
});

const ChartWrapper = styled('div')({
    '@media print': {
        height: '280px !important',
    },
});

const SERIES = [
    { platform: POST_CHANNEL.FACEBOOK, label: 'Facebook', color: BRAND_TEAL },
    { platform: POST_CHANNEL.INSTAGRAM, label: 'Instagram', color: '#98CBAD' },
];

const platformsIn = data => new Set(data.flatMap(item => item.breakdown.map(b => b.platform)));

const createChartData = (labels, data) => ({
    labels,
    datasets: SERIES.filter(series => platformsIn(data).has(series.platform)).map(series => ({
        label: series.label,
        data: data.map(item => item.breakdown.find(b => b.platform === series.platform)?.value ?? null),
        borderColor: series.color,
        backgroundColor: series.color,
        pointBackgroundColor: series.color,
        fill: true,
    })),
});

const lineOptions = {
    elements: {
        point: {
            radius: 3,
            hoverRadius: 5,
        },
    },
    plugins: {
        legend: {
            reverse: false,
        },
    },
    scales: {
        y: {
            beginAtZero: true,
        },
    },
};

const SocialPageTrend = memo(({ title, data, language, grouping, wrapperId }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 1024px)');

    const labels = useMemo(
        () =>
            data.map(d =>
                format(parseISO(d.date), getChartDateFormat(grouping), { locale: I18N_DATE_FORMAT[language] }),
            ),
        [language, data, grouping],
    );

    const chartData = useMemo(() => createChartData(labels, data), [labels, data]);

    return (
        <PaperCss elevation={1}>
            <Typography variant="title1" sx={{ marginBottom: '50px' }}>
                {t(title)}
            </Typography>
            <TableContainer>
                <ChartWrapper
                    id={wrapperId}
                    style={{
                        position: 'relative',
                        height: isMobile ? '400px' : '280px',
                        ...(isMobile && { width: '900px' }),
                    }}
                >
                    {labels.length ? (
                        <Line
                            parentId={wrapperId}
                            data={chartData}
                            labels={labels}
                            legend={t(title)}
                            optionsProps={lineOptions}
                        />
                    ) : null}
                </ChartWrapper>
            </TableContainer>
        </PaperCss>
    );
});

SocialPageTrend.displayName = 'SocialPageTrend';

export default SocialPageTrend;
